/**
 * Selection-mode UI primitives — Layer 3 of the three-layer design.
 *
 * Ready-to-use components that consume `useSelectionMode()` state and
 * render the standard "Select → Done" toggle + bulk actions bar. Callers
 * who want a turnkey selection UX drop these in alongside `SDataTable`;
 * callers who need custom layouts ignore them and render their own
 * section headers using the hook directly.
 *
 * Both components are thin compositions of MUI primitives + theme tokens —
 * zero hardcoded colours.
 */

import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  type ButtonProps,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { SelectionModeState } from './useSelectionMode';

// ============================================================================
// SelectionModeToggle
// ============================================================================

export interface SelectionModeToggleProps {
  /** The hook state returned by `useSelectionMode()`. */
  selection: SelectionModeState;
  /** Label shown while not in mode. Default: `'Select'`. */
  selectLabel?: string;
  /** Label shown while in mode. Default: `'Done'`. */
  doneLabel?: string;
  /** Button size. Default: `'small'` to match compact section headers. */
  size?: ButtonProps['size'];
  /** Optional `data-testid` for the button. */
  testId?: string;
}

/**
 * A single button that flips between "Select" (enter mode) and "Done"
 * (exit mode + clear selection). Place in a section header next to the
 * title, or wherever the caller's layout warrants.
 *
 * @example
 * ```tsx
 * const selection = useSelectionMode();
 * <SelectionModeToggle selection={selection} />
 * ```
 */
export function SelectionModeToggle({
  selection,
  selectLabel = 'Select',
  doneLabel = 'Done',
  size = 'small',
  testId,
}: SelectionModeToggleProps) {
  return (
    <Button
      size={size}
      variant="outlined"
      onClick={selection.mode ? selection.exit : selection.enter}
      sx={{ textTransform: 'none' }}
      aria-pressed={selection.mode}
      data-testid={testId}
    >
      {selection.mode ? doneLabel : selectLabel}
    </Button>
  );
}

// ============================================================================
// SelectionActionsBar
// ============================================================================

export interface SelectionActionsBarProps {
  /** The hook state returned by `useSelectionMode()`. */
  selection: SelectionModeState;
  /**
   * Bulk-action buttons / IconButtons. Rendered right of the counter chip
   * (and right of the built-in Delete button when `onDeleteSelected` is
   * set). Per-table custom actions (Move to folder, Export, Archive…).
   */
  children?: React.ReactNode;
  /** Hide the counter chip. Default: false. */
  hideCounter?: boolean;
  /**
   * Opt-in built-in "Delete selected" action — the one bulk action nearly
   * every table has. When provided, an error-coloured Delete IconButton
   * renders between the counter chip and `children`, firing with the
   * current `selectedIds`. Confirmation dialogs stay caller-owned: put the
   * confirm flow inside this handler.
   */
  onDeleteSelected?: (ids: string[]) => void | Promise<void>;
  /** Tooltip / aria-label for the built-in delete. Default: `'Delete selected'`. */
  deleteLabel?: string;
  /**
   * Busy state while a bulk operation runs: the built-in delete shows a
   * spinner and all bar actions are wrapped in a disabled fieldset-like
   * state (the built-in delete disables; `children` receive it via their
   * own props — callers disable custom actions themselves).
   */
  busy?: boolean;
  /** `data-testid` for the built-in delete. Default: `'selection-delete-selected'`. */
  deleteTestId?: string;
  /**
   * Always occupy a fixed-height (36px) slot, even with nothing selected —
   * the bar's appearance must not shift the table below it (no layout
   * jump). Use on pages where the bar sits between the title and the table
   * (checkboxReveal lists). Default: false (legacy render-nothing).
   */
  reserveSpace?: boolean;
}

/**
 * A compact bar rendered only when `mode && hasSelection` — shows a
 * primary-coloured counter Chip, the optional built-in Delete-selected
 * action, then the caller's bulk-action controls. Hidden silently
 * otherwise, so callers can safely place it unconditionally in their
 * layout.
 *
 * @example
 * ```tsx
 * <SelectionActionsBar
 *   selection={selection}
 *   onDeleteSelected={(ids) => confirmThenDelete(ids)}
 *   busy={bulkBusy}
 * >
 *   <Button size="small" variant="outlined" onClick={openMoveDialog}>
 *     Move to folder
 *   </Button>
 * </SelectionActionsBar>
 * ```
 */
export function SelectionActionsBar({
  selection,
  children,
  hideCounter = false,
  onDeleteSelected,
  deleteLabel = 'Delete selected',
  busy = false,
  deleteTestId = 'selection-delete-selected',
  reserveSpace = false,
}: SelectionActionsBarProps) {
  if (!selection.mode || !selection.hasSelection) {
    // Reserved slot: identical height whether the bar is shown or not, so
    // the table never jumps when a selection starts/ends.
    return reserveSpace ? (
      <Box sx={{ height: 36 }} aria-hidden data-testid="selection-bar-slot" />
    ) : null;
  }
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      role="toolbar"
      aria-label="Bulk actions"
      sx={reserveSpace ? { height: 36 } : undefined}
    >
      {!hideCounter && (
        <Chip
          label={selection.selectedIds.length}
          size="small"
          color="primary"
          sx={{ fontWeight: 'fontWeightMedium' }}
        />
      )}
      {onDeleteSelected && (
        <Tooltip title={deleteLabel}>
          <span>
            <IconButton
              color="error"
              size="small"
              aria-label={deleteLabel}
              disabled={busy}
              onClick={() => onDeleteSelected(selection.selectedIds)}
              data-testid={deleteTestId}
            >
              {busy ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '& .MuiButton-root': {
            minHeight: 28,
            height: 28,
            px: 1.25,
            py: 0,
            textTransform: 'none',
          },
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}
